import React, { useEffect, useState } from 'react';
import {
    Box, Button, Chip, IconButton, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftService } from '../../services/shift.service';
import ShiftDetailDialog from './ShiftDetailDialog';

const ShiftListPage: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();

    const canCreate = usePermission(PERMISSIONS.SHIFT_MANAGE);
    const canEdit   = usePermission(PERMISSIONS.SHIFT_MANAGE);
    const canDelete = usePermission(PERMISSIONS.SHIFT_MANAGE);

    const [shifts, setShifts] = useState<Shift[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingShift, setEditingShift] = useState<Shift | null>(null);

    useEffect(() => {
        if (selectedInstitutionId) {
            fetchShifts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedInstitutionId]);

    const fetchShifts = async () => {
        if (!selectedInstitutionId) return;
        setLoading(true);
        try {
            const data = await ShiftService.list(selectedInstitutionId);
            setShifts(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch shifts');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingShift(null);
        setDialogOpen(true);
    };

    const handleEdit = (shift: Shift) => {
        setEditingShift(shift);
        setDialogOpen(true);
    };

    const handleDelete = async (shift: Shift) => {
        if (!selectedInstitutionId) return;
        if (!window.confirm(`Delete shift "${shift.name}"?`)) return;
        try {
            await ShiftService.remove(selectedInstitutionId, shift.id);
            toast.showSuccess('Shift deleted');
            await fetchShifts();
        } catch (err) {
            toast.showApiError(err, 'Failed to delete shift');
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingShift(null);
    };

    const handleSaved = () => {
        fetchShifts();
    };

    // Format "HH:mm:ss" → "HH:mm"
    const fmtTime = (t: string) => (t ? t.substring(0, 5) : '—');

    if (loading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;

    return (
        <Box data-help-page="shifts.list.page" sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Shifts</Typography>
                {canCreate && (
                    <Button data-help="shifts.list.new" variant="contained" color="primary" onClick={handleCreate}>
                        + New Shift
                    </Button>
                )}
            </Box>

            {shifts.length === 0 ? (
                <Typography>No shifts found</Typography>
            ) : (
                <TableContainer data-help-section="shifts.list.table" component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="shifts.list.col.name">Name</TableCell>
                                <TableCell data-help="shifts.list.col.code">Code</TableCell>
                                <TableCell data-help="shifts.list.col.start">Start</TableCell>
                                <TableCell data-help="shifts.list.col.end">End</TableCell>
                                <TableCell data-help="shifts.list.col.crossMidnight">Cross-midnight</TableCell>
                                <TableCell data-help="shifts.list.col.active">Active</TableCell>
                                {(canEdit || canDelete) && <TableCell data-help="shifts.list.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shifts.map(shift => (
                                <TableRow key={shift.id}>
                                    <TableCell>{shift.name}</TableCell>
                                    <TableCell>{shift.code}</TableCell>
                                    <TableCell>{fmtTime(shift.startTime)}</TableCell>
                                    <TableCell>{fmtTime(shift.endTime)}</TableCell>
                                    <TableCell>
                                        {shift.crossMidnight
                                            ? <Chip label="Yes" color="warning" size="small" />
                                            : <Chip label="No" size="small" />}
                                    </TableCell>
                                    <TableCell>
                                        {shift.isActive
                                            ? <Chip label="Active" color="success" size="small" />
                                            : <Chip label="Inactive" size="small" />}
                                    </TableCell>
                                    {(canEdit || canDelete) && (
                                        <TableCell>
                                            {canEdit && (
                                                <IconButton
                                                    data-help="shifts.list.action.edit"
                                                    onClick={() => handleEdit(shift)}
                                                    color="primary"
                                                    aria-label="edit"
                                                    size="small"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            {canDelete && (
                                                <IconButton
                                                    data-help="shifts.list.action.delete"
                                                    onClick={() => handleDelete(shift)}
                                                    color="error"
                                                    aria-label="delete"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {selectedInstitutionId && (
                <ShiftDetailDialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    shift={editingShift}
                    institutionId={selectedInstitutionId}
                    onSaved={handleSaved}
                />
            )}
        </Box>
    );
};

export default ShiftListPage;
